/**
 * useTwoClickConfirm
 * Shared arm-then-confirm interaction for destructive actions: the first
 * `trigger()` arms the action (returning `armed: true` so the caller can show a
 * confirm affordance), the second confirms and runs `onConfirm`. If the second
 * click doesn't arrive within `resetMs` the armed state auto-resets. The timer
 * is cleared on unmount. Used by ContentBlockWrapper (block delete) and the
 * course editor's lesson row (lesson remove) so the confirm timing stays in one
 * place.
 */

import { useEffect, useRef, useState } from 'react';

const DEFAULT_CONFIRM_RESET_MS = 3000;

export interface UseTwoClickConfirmResult {
  /** True once armed (first click) and before confirm or auto-reset. */
  armed: boolean;
  /** Arms on the first call; runs `onConfirm` and disarms on the second. */
  trigger: () => void;
  /** Cancels the pending timer and disarms (e.g. on mouse leave). */
  reset: () => void;
}

export function useTwoClickConfirm(
  onConfirm: () => void,
  options?: { resetMs?: number }
): UseTwoClickConfirmResult {
  const resetMs = options?.resetMs ?? DEFAULT_CONFIRM_RESET_MS;
  const [armed, setArmed] = useState(false);
  const resetTimeoutRef = useRef<number | null>(null);

  const clearResetTimer = () => {
    if (resetTimeoutRef.current !== null) {
      window.clearTimeout(resetTimeoutRef.current);
      resetTimeoutRef.current = null;
    }
  };

  useEffect(() => clearResetTimer, []);

  const reset = () => {
    clearResetTimer();
    setArmed(false);
  };

  const trigger = () => {
    if (armed) {
      clearResetTimer();
      onConfirm();
      setArmed(false);
    } else {
      setArmed(true);
      clearResetTimer();
      resetTimeoutRef.current = window.setTimeout(() => {
        setArmed(false);
        resetTimeoutRef.current = null;
      }, resetMs);
    }
  };

  return { armed, trigger, reset };
}
